import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Shield, TrendingUp, History, Lock, LogOut, ChevronRight, RefreshCw, CheckCircle, Info, Mail, Phone, MapPin, Save, Settings, FileText, Trash2, Plus } from 'lucide-react';
import { CONFIG } from '../utils/config';

const AdminDashboard = () => {
    const [rates, setRates] = useState({ sell_rate: 0, buy_rate: 0, currency: 'USDT' });
    const [selectedCurrency, setSelectedCurrency] = useState('USDT');
    const [contacts, setContacts] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [pendingTestimonials, setPendingTestimonials] = useState([]);
    const [siteSettings, setSiteSettings] = useState({ contact_email: '', contact_whatsapp: '', contact_location: '' });
    const [blogs, setBlogs] = useState([]);
    const [newBlog, setNewBlog] = useState({ title: '', content: '', author: 'Doumene M. Rosvel', category: 'Guide' });
    const [creatingBlog, setCreatingBlog] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [isBackendOnline, setIsBackendOnline] = useState(true);
    const navigate = useNavigate();
    
    const token = localStorage.getItem('admin_token');

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            
            const [healthRes, ratesRes, contactRes, transRes, testimonialsRes, settingsRes, blogsRes] = await Promise.all([
                axios.get(`${CONFIG.API_BASE}/health`),
                axios.get(`${CONFIG.API_BASE}/rates/current?currency=${selectedCurrency}`),
                axios.get(`${CONFIG.API_BASE}/admin/contacts`, config),
                axios.get(`${CONFIG.API_BASE}/transactions?limit=10`),
                axios.get(`${CONFIG.API_BASE}/admin/testimonials/pending`, config),
                axios.get(`${CONFIG.API_BASE}/settings`),
                axios.get(`${CONFIG.API_BASE}/blogs`).catch(() => ({ data: [] }))
            ]);

            setIsBackendOnline(healthRes.data?.status === 'healthy');
            setRates(ratesRes.data);
            setContacts(contactRes.data);
            setTransactions(transRes.data);
            setPendingTestimonials(testimonialsRes.data);
            setSiteSettings(prev => ({ ...prev, ...settingsRes.data }));
            setBlogs(blogsRes.data || []);
        } catch (err) {
            console.error("Dashboard error:", err);
            setIsBackendOnline(false);
            if (err.response?.status === 401) {
                navigate('/admin/login');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, [selectedCurrency]);

    useEffect(() => {
        if (!token) navigate('/admin/login');
        fetchDashboardData();
        const interval = setInterval(fetchDashboardData, 30000);
        return () => clearInterval(interval);
    }, []);

    const updateMarketRates = async (e) => {
        e.preventDefault();
        setUpdating(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.put(`${CONFIG.API_BASE}/admin/rates`, {
                sell_rate: rates.sell_rate,
                buy_rate: rates.buy_rate,
                currency: selectedCurrency
            }, config);
            
            alert("RATES SYNCED TO PRODUCTION CLUSTER");
            fetchDashboardData();
        } catch (err) {
            alert("Protocol Update Failed: Check Admin Permissions");
        } finally {
            setUpdating(false);
        }
    };

    const approveTestimonial = async (id) => {
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.put(`${CONFIG.API_BASE}/admin/testimonials/${id}/approve`, {}, config);
            fetchDashboardData();
        } catch (err) {
            alert("Approve Failed");
        }
    };

    const deleteTestimonial = async (id) => {
        if (!window.confirm("Delete this review forever?")) return;
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.delete(`${CONFIG.API_BASE}/admin/testimonials/${id}`, config);
            fetchDashboardData();
        } catch (err) {
            alert("Delete Failed");
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin/login');
    };

    const saveSiteSettings = async (e) => {
        e.preventDefault();
        setSavingSettings(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const updates = Object.entries(siteSettings).map(([key, value]) => ({ key, value }));
            await axios.put(`${CONFIG.API_BASE}/admin/settings`, updates, config);
            alert("CONTACT INFO SYNCED TO PRODUCTION");
            fetchDashboardData();
        } catch (err) {
            alert("Settings Update Failed: Check Admin Permissions");
        } finally {
            setSavingSettings(false);
        }
    };

    const BLOG_TEMPLATES = {
        "Guide/Tutorial": `---\ntitle: "Guide Title"\nauthor: "Doumene M. Rosvel"\ndate: "${new Date().toISOString().split('T')[0]}"\ncategory: "Guide"\nexcerpt: "A brief summary of this guide."\nslug: "guide-title"\nread_time: "5 min"\ncover_image: "/assets/blog/default_cover.webp"\n---\n\n## Introduction\nExplain the process clearly here...\n\n## Step-by-Step\n1. First step\n2. Second step\n\n## Conclusion\nWrap up the guide. Ready to trade? Head over to our [Smart Rate Calculator](/#calculator) to get started.`,
        "Market Analysis": `---\ntitle: "Market Analysis: [Month Year]"\nauthor: "Doumene M. Rosvel"\ndate: "${new Date().toISOString().split('T')[0]}"\ncategory: "Analysis"\nexcerpt: "Insights into the crypto-to-XAF market."\nslug: "market-analysis-month-year"\nread_time: "4 min"\ncover_image: "/assets/blog/default_cover.webp"\n---\n\n## Overview\nWhat is happening in the crypto-to-XAF market?\n\n## Key Trends\n- Trend 1\n- Trend 2\n\n## Prediction\nWhere is the market heading? Check the live rates on the [Home Page](/) and make your move.`,
        "Security Alert": `---\ntitle: "Security Alert: [Topic]"\nauthor: "Doumene M. Rosvel"\ndate: "${new Date().toISOString().split('T')[0]}"\ncategory: "Security"\nexcerpt: "Important security update to protect your assets."\nslug: "security-alert-topic"\nread_time: "3 min"\ncover_image: "/assets/blog/default_cover.webp"\n---\n\n## The Threat\nDescribe the latest scam or security threat.\n\n## How to Protect Yourself\n- Do not share OTPs.\n- Verify official Zaptopay channels.\n\nStay safe and securely exchange with [Zaptopay](/#calculator).`,
        "Founder's Vision": `---\ntitle: "A Message from the Founder"\nauthor: "Doumene M. Rosvel"\ndate: "${new Date().toISOString().split('T')[0]}"\ncategory: "Vision"\nexcerpt: "Reflecting on our growth in Cameroon."\nslug: "message-from-founder"\nread_time: "6 min"\ncover_image: "/assets/blog/default_cover.webp"\n---\n\n## Our Journey\nReflecting on our growth in Cameroon...\n\n## The Road Ahead\nWhat's next for Zaptopay.\n\n*Doumene M. Rosvel*`,
        "Freelancer Guide": `---\ntitle: "How Freelancers can maximize earnings"\nauthor: "Doumene M. Rosvel"\ndate: "${new Date().toISOString().split('T')[0]}"\ncategory: "Guide"\nexcerpt: "Learn how to get paid globally and convert to XAF."\nslug: "freelancer-guide"\nread_time: "5 min"\ncover_image: "/assets/blog/default_cover.webp"\n---\n\n## Receiving Payments\nHow to get paid by global clients in Crypto.\n\n## Converting to XAF\nWhy Zaptopay gives the best rates. Calculate your potential earnings on the [Zaptopay Calculator](/#calculator).`
    };

    const applyTemplate = (templateName) => {
        setNewBlog({ ...newBlog, content: BLOG_TEMPLATES[templateName], title: `New ${templateName}` });
    };

    const handleCreateBlog = async (e) => {
        e.preventDefault();
        if (!newBlog.title || !newBlog.content) {
            alert("Title and Content are required.");
            return;
        }
        setCreatingBlog(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post(`${CONFIG.API_BASE}/admin/blogs`, newBlog, config);
            alert("Blog published successfully!");
            setNewBlog({ title: '', content: '', author: 'Doumene M. Rosvel', category: 'Guide' });
            fetchDashboardData();
        } catch (err) {
            alert("Failed to publish blog");
        } finally {
            setCreatingBlog(false);
        }
    };
    
    const handleDeleteBlog = async (id) => {
        if(!window.confirm("Permanently delete this blog?")) return;
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.delete(`${CONFIG.API_BASE}/admin/blogs/${id}`, config);
            fetchDashboardData();
        } catch(err) {
            alert("Failed to delete blog");
        }
    };

    if (loading && rates.sell_rate === 0) return (
       <div className="min-h-screen bg-bg-deep flex items-center justify-center p-6">
           <div className="flex flex-col items-center gap-6">
                <RefreshCw className="text-primary animate-spin" size={48} />
                <div className="text-[10px] font-black uppercase tracking-[0.4em] text-white">Initializing Dashboard Protocol...</div>
           </div>
       </div>
    );

    return (
        <div className="min-h-screen bg-bg-deep text-white pt-28 md:pt-48 pb-12 px-6 md:px-12 relative overflow-hidden font-header">
            {/* Nav Header */}
            <div className="flex flex-col sm:flex-row gap-6 justify-between sm:items-center mb-8 sm:mb-16 relative z-10 glass p-4 sm:p-6 border-white/5">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 glass border-primary/20 flex items-center justify-center">
                        <Shield className="text-primary" size={24} />
                    </div>
                    <div>
                        <div className="text-sm font-black uppercase tracking-widest text-text-main">ZAPTO_DASHBOARD</div>
                        <div className="flex items-center gap-2 mt-1">
                            <div className={`w-1.5 h-1.5 rounded-full ${isBackendOnline ? 'bg-primary animate-pulse' : 'bg-red-500'}`}></div>
                            <div className="text-[9px] text-primary font-bold uppercase tracking-[0.2em]">Operator: {localStorage.getItem('admin_user')} • {isBackendOnline ? 'Verified Session' : 'Offline Mode'}</div>
                        </div>
                    </div>
                </div>
                <button 
                    onClick={handleLogout}
                    className="flex items-center justify-center gap-2 text-text-muted hover:text-red-500 transition-colors uppercase font-black text-[10px] tracking-widest px-6 py-3 border border-white/5 hover:bg-red-500/10 w-full sm:w-auto"
                >
                    <LogOut size={14} />
                    Terminate Session
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 relative z-10">
                {/* Left Column: Rates + Contact Info */}
                <div className="lg:col-span-1 space-y-8">
                    <div className="glass p-6 sm:p-10 border-primary/20">
                        <div className="flex items-center justify-between mb-8">
                            <div className="flex items-center gap-3">
                                <TrendingUp className="text-primary" size={20} />
                                <h2 className="text-lg font-black uppercase tracking-widest">Global Rate Sync</h2>
                            </div>
                            <select 
                                value={selectedCurrency}
                                onChange={(e) => setSelectedCurrency(e.target.value)}
                                className="bg-black/40 border border-white/15 px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest focus:outline-none focus:border-primary transition-all text-primary cursor-pointer hover:bg-primary/10"
                            >
                                {['USDT', 'BTC', 'ETH', 'SOL', 'BNB'].map(curr => (
                                    <option key={curr} value={curr} className="bg-black text-white">{curr}</option>
                                ))}
                            </select>
                        </div>

                        <form onSubmit={updateMarketRates} className="space-y-8">
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-4 opacity-70">Production Sell Rate (XAF)</label>
                                <div className="relative group">
                                    <input 
                                        type="number"
                                        value={rates.sell_rate}
                                        onChange={(e) => setRates({...rates, sell_rate: parseFloat(e.target.value)})}
                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-6 py-5 text-2xl font-black text-primary focus:outline-none focus:border-primary transition-all group-hover:bg-primary/5"
                                    />
                                    <div className="absolute right-6 top-1/2 -translate-y-1/2 text-[10px] font-black text-text-muted uppercase">XAF/{selectedCurrency}</div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-4 opacity-70">Production Buy Rate (XAF)</label>
                                <div className="relative group">
                                    <input 
                                        type="number"
                                        value={rates.buy_rate}
                                        onChange={(e) => setRates({...rates, buy_rate: parseFloat(e.target.value)})}
                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-6 py-5 text-2xl font-black text-white focus:outline-none focus:border-primary transition-all group-hover:bg-white/5"
                                    />
                                    <div className="absolute right-6 top-1/2 -translate-y-1/2 text-[10px] font-black text-text-muted uppercase">XAF/{selectedCurrency}</div>
                                </div>
                            </div>

                            <button 
                                type="submit"
                                disabled={updating}
                                className="w-full py-6 bg-primary text-bg-deep font-black uppercase tracking-[0.2em] text-xs rounded-xl hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_40px_rgba(var(--primary-rgb),0.3)] flex items-center justify-center gap-3"
                            >
                                {updating ? <RefreshCw className="animate-spin" size={18} /> : (
                                    <>
                                        Broadcast New Rates
                                        <ChevronRight size={18} />
                                    </>
                                )}
                            </button>
                        </form>

                        <div className="mt-12 p-6 bg-primary/5 border border-primary/10 rounded-xl">
                            <div className="flex gap-4">
                                <Info className="text-primary shrink-0" size={18} />
                                <div className="text-[10px] text-white/50 leading-relaxed font-bold uppercase tracking-widest">
                                    Updating these rates will immediately sync with the WhatsApp Preview modal and the frontend calculators for all global users.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Info Editor */}
                    <div className="glass p-6 sm:p-10 border-secondary/20">
                        <div className="flex items-center gap-3 mb-8">
                            <Settings className="text-secondary" size={20} />
                            <h2 className="text-lg font-black uppercase tracking-widest">Contact Info</h2>
                        </div>

                        <form onSubmit={saveSiteSettings} className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-3 opacity-70">
                                    <Mail size={12} className="inline mr-2" />Email Address
                                </label>
                                <input
                                    type="email"
                                    value={siteSettings.contact_email || ''}
                                    onChange={(e) => setSiteSettings({ ...siteSettings, contact_email: e.target.value })}
                                    className="w-full bg-black/40 border border-white/10 rounded-xl px-5 py-4 text-sm font-bold text-white focus:outline-none focus:border-secondary transition-all"
                                    placeholder="your@email.com"
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-3 opacity-70">
                                    <Phone size={12} className="inline mr-2" />WhatsApp Number
                                </label>
                                <input
                                    type="text"
                                    value={siteSettings.contact_whatsapp || ''}
                                    onChange={(e) => setSiteSettings({ ...siteSettings, contact_whatsapp: e.target.value })}
                                    className="w-full bg-black/40 border border-white/10 rounded-xl px-5 py-4 text-sm font-bold text-white focus:outline-none focus:border-secondary transition-all"
                                    placeholder="237XXXXXXXXX"
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-3 opacity-70">
                                    <MapPin size={12} className="inline mr-2" />Location / Address
                                </label>
                                <input
                                    type="text"
                                    value={siteSettings.contact_location || ''}
                                    onChange={(e) => setSiteSettings({ ...siteSettings, contact_location: e.target.value })}
                                    className="w-full bg-black/40 border border-white/10 rounded-xl px-5 py-4 text-sm font-bold text-white focus:outline-none focus:border-secondary transition-all"
                                    placeholder="City, Country"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={savingSettings}
                                className="w-full py-5 bg-secondary text-bg-deep font-black uppercase tracking-[0.2em] text-xs rounded-xl hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_40px_rgba(0,212,255,0.3)] flex items-center justify-center gap-3"
                            >
                                {savingSettings ? <RefreshCw className="animate-spin" size={18} /> : (
                                    <>
                                        <Save size={16} />
                                        Save Contact Info
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Right Column: Transactions & Contacts */}
                <div className="lg:col-span-2 space-y-12">
                    
                    {/* Blog Content Management */}
                    <div className="glass p-6 sm:p-10 border-white/5">
                        <div className="flex justify-between items-center mb-8">
                            <div className="flex items-center gap-3">
                                <FileText className="text-secondary" size={20} />
                                <h2 className="text-lg font-black uppercase tracking-widest">Blog Management Engine</h2>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                            <div className="bg-black/20 p-6 border border-white/5 rounded-xl">
                                <h3 className="text-xs font-black text-text-muted mb-4 uppercase tracking-widest">Publish New Article</h3>
                                
                                <div className="flex flex-wrap gap-2 mb-6">
                                    {Object.keys(BLOG_TEMPLATES).map(tmpl => (
                                        <button 
                                            key={tmpl}
                                            onClick={(e) => { e.preventDefault(); applyTemplate(tmpl); }}
                                            className="px-3 py-1.5 text-[9px] font-black uppercase tracking-wider border border-primary/20 bg-primary/5 text-primary hover:bg-primary/20 transition-colors rounded"
                                        >
                                            + {tmpl}
                                        </button>
                                    ))}
                                </div>

                                <form onSubmit={handleCreateBlog} className="space-y-4">
                                    <input 
                                        type="text"
                                        placeholder="Post Title"
                                        value={newBlog.title}
                                        onChange={e => setNewBlog({...newBlog, title: e.target.value})}
                                        className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-sm font-bold text-white focus:border-secondary transition-all"
                                    />
                                    <textarea 
                                        placeholder="Markdown Content (with Frontmatter)"
                                        value={newBlog.content}
                                        onChange={e => setNewBlog({...newBlog, content: e.target.value})}
                                        className="w-full h-64 bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-sm font-mono text-white focus:border-secondary transition-all resize-y"
                                    ></textarea>
                                    
                                    <button 
                                        type="submit"
                                        disabled={creatingBlog}
                                        className="w-full py-4 bg-secondary text-bg-deep font-black uppercase tracking-[0.2em] text-[10px] rounded-lg hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                                    >
                                        {creatingBlog ? <RefreshCw className="animate-spin" size={14} /> : <><Plus size={14} /> Publish to Live Server</>}
                                    </button>
                                </form>
                            </div>

                            <div className="bg-black/20 border border-white/5 rounded-xl overflow-hidden flex flex-col max-h-[500px]">
                                <h3 className="text-xs font-black text-text-muted m-6 uppercase tracking-widest shrink-0">Live Articles</h3>
                                <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-3">
                                    {blogs.length === 0 ? (
                                        <div className="text-[10px] text-text-muted opacity-50 uppercase tracking-widest text-center py-10">No articles published</div>
                                    ) : blogs.map(blog => (
                                        <div key={blog.id} className="p-4 bg-white/5 border border-white/5 rounded-lg flex justify-between items-center group hover:border-white/20 transition-all">
                                            <div>
                                                <div className="text-sm font-bold truncate max-w-[200px] sm:max-w-[250px]">{blog.title}</div>
                                                <div className="text-[10px] text-primary uppercase tracking-widest mt-1">{new Date(blog.created_at).toLocaleDateString()}</div>
                                            </div>
                                            <button 
                                                onClick={() => handleDeleteBlog(blog.id)}
                                                className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-red-500 hover:bg-red-500/10 rounded transition-all"
                                                title="Delete Blog"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Public Ledger Overview */}
                    <div className="glass p-6 sm:p-10 border-white/5">
                        <div className="flex justify-between items-center mb-10">
                            <div className="flex items-center gap-3">
                                <History className="text-secondary" size={20} />
                                <h2 className="text-lg font-black uppercase tracking-widest">Transaction Pipeline</h2>
                            </div>
                            <button className="text-[10px] font-black uppercase tracking-widest text-primary hover:underline">View All Pipeline</button>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-white/5 text-[10px] font-black uppercase tracking-widest text-text-muted">
                                        <th className="py-4 px-2">TXID_HASH</th>
                                        <th className="py-4 px-2">VOLUME</th>
                                        <th className="py-4 px-2">STATUS</th>
                                        <th className="py-4 px-2">TIMESTAMP</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((tx, i) => (
                                        <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                                            <td className="py-5 px-2 font-mono text-[10px] opacity-40 group-hover:opacity-100 transition-opacity">
                                                ZAP_{tx.tx_hash ? tx.tx_hash.substring(0, 12) : "GEN_" + tx.id.substring(0, 8)}...
                                            </td>
                                            <td className="py-5 px-2">
                                                <div className="text-xs font-black">${tx.amount_usd.toLocaleString()}</div>
                                                <div className="text-[10px] text-text-muted uppercase font-bold mt-1">→ {tx.amount_xaf.toLocaleString()} XAF</div>
                                            </td>
                                            <td className="py-5 px-2">
                                                <div className="flex items-center gap-2 text-[10px] font-black text-primary">
                                                    <CheckCircle size={10} />
                                                    {tx.status.toUpperCase()}
                                                </div>
                                            </td>
                                            <td className="py-5 px-2 text-[10px] font-bold text-text-muted">
                                                {new Date(tx.created_at).toLocaleTimeString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Testimonial Moderation Pipeline */}
                    <div className="glass p-6 sm:p-10 border-primary/10 bg-primary/[0.02]">
                        <div className="flex items-center gap-3 mb-10">
                            <CheckCircle className="text-primary" size={20} />
                            <h2 className="text-lg font-black uppercase tracking-widest">Testimonial Moderation</h2>
                        </div>

                        <div className="space-y-6">
                            {pendingTestimonials.length === 0 ? (
                                <div className="text-center py-10 text-[10px] font-black uppercase tracking-widest text-text-muted opacity-30">ALL REVIEWS PROCESSED</div>
                            ) : pendingTestimonials.map((t, i) => (
                                <div key={i} className="flex flex-col sm:flex-row gap-4 sm:gap-6 p-4 sm:p-6 bg-white/5 border border-white/10 group hover:border-primary/30 transition-all rounded-xl">
                                    <div className="w-12 h-12 glass border-white/10 flex items-center justify-center text-primary font-black shrink-0 rounded-full mx-auto sm:mx-0">
                                        {t.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <h4 className="text-xs font-black uppercase tracking-widest text-white">{t.name}</h4>
                                                <p className="text-[10px] text-primary uppercase mt-1">{t.role || 'Verified User'}</p>
                                            </div>
                                            <div className="flex gap-3">
                                                <button 
                                                    onClick={() => approveTestimonial(t.id)}
                                                    className="px-4 py-2 bg-primary/20 text-primary text-[9px] font-black uppercase tracking-widest hover:bg-primary hover:text-black transition-all rounded"
                                                >
                                                    Approve
                                                </button>
                                                <button 
                                                    onClick={() => deleteTestimonial(t.id)}
                                                    className="px-4 py-2 bg-red-500/10 text-red-500 text-[9px] font-black uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all rounded"
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        </div>
                                        <p className="text-sm text-text-muted leading-relaxed font-medium mt-4 group-hover:text-white transition-colors">"{t.content}"</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Operational Inquiries (Contacts) */}
                    <div className="glass p-6 sm:p-10 border-white/5">
                        <div className="flex items-center gap-3 mb-10">
                            <Mail className="text-primary" size={20} />
                            <h2 className="text-lg font-black uppercase tracking-widest">Operator Inquiries</h2>
                        </div>

                        <div className="space-y-6">
                            {contacts.length === 0 ? (
                                <div className="text-center py-10 text-[10px] font-black uppercase tracking-widest text-text-muted opacity-30">NO ACTIVE INQUIRIES RECORDED</div>
                            ) : contacts.map((contact, i) => (
                                <div key={i} className="flex flex-col sm:flex-row gap-4 sm:gap-6 p-4 sm:p-6 bg-white/5 border border-white/10 group hover:border-primary/30 transition-all rounded-xl">
                                    <div className="w-12 h-12 glass border-white/10 flex items-center justify-center text-primary font-black shrink-0 mx-auto sm:mx-0">
                                        {contact.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <h4 className="text-xs font-black uppercase tracking-widest text-white">{contact.name}</h4>
                                                <div className="flex items-center gap-3 mt-1">
                                                    <p className="text-[10px] text-primary lowercase">{contact.email}</p>
                                                    <a 
                                                        href={`https://wa.me/2376?text=Hi ${contact.name}, regarding your Zaptopay inquiry: "${contact.message.substring(0, 50)}..."`} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="text-[9px] font-black uppercase tracking-widest text-[#00ff88] hover:underline flex items-center gap-1"
                                                    >
                                                        Reply WhatsApp ⚡
                                                    </a>
                                                </div>
                                            </div>
                                            <span className="text-[10px] font-bold text-text-muted uppercase tracking-widest">{new Date(contact.created_at).toLocaleDateString()}</span>
                                        </div>
                                        <p className="text-sm text-text-muted leading-relaxed font-medium mt-4 group-hover:text-white transition-colors">{contact.message}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Global Background Elements */}
            <div className="fixed top-0 left-0 w-full h-full -z-10 pointer-events-none">
                <div className="absolute top-[20%] right-[-10%] w-[500px] h-[500px] bg-secondary/5 blur-[150px] rounded-full"></div>
                <div className="absolute bottom-[10%] left-[-10%] w-[500px] h-[500px] bg-primary/5 blur-[150px] rounded-full"></div>
            </div>
        </div>
    );
};

export default AdminDashboard;
